import { Box, BoxProps, Tooltip } from "@mui/material";
import React from "react";

interface IMetadataChipProps extends BoxProps {
    /**
     * Icon to display before the text
     */
    icon: React.ReactElement<Record<string, unknown>>;
    /**
     * Text to display in the chip
     */
    label: string;
    /**
     * Background color for the chip
     */
    backgroundColor: string;
    /**
     * Text color for the chip
     */
    color: string;
    /**
     * Background color on hover
     */
    hoverBackgroundColor: string;
    /**
     * Optional href to make the chip a link
     */
    href?: string;
}

/**
 * Reusable metadata chip component for displaying small labeled items with icons
 * Used for clause checklists, scopes, and other metadata
 */
export const MetadataChip = ({
    icon,
    label,
    backgroundColor,
    color,
    hoverBackgroundColor,
    href,
    ...props
}: IMetadataChipProps) => {
    const MAX_CHARS = 50;
    const isTruncated = label.length > MAX_CHARS;
    const displayLabel = isTruncated
        ? `${label.slice(0, MAX_CHARS).trim()}...`
        : label;

    const chipContent = (
        <>
            {React.cloneElement(icon, {
                style: {
                    width: 14,
                    height: 14,
                    flexShrink: 0,
                },
            })}
            {displayLabel}
        </>
    );

    const baseStyles = {
        display: "inline-flex",
        alignItems: "center",
        backgroundColor,
        color,
        padding: "0.2rem 0.4rem",
        borderRadius: "4px",
        fontSize: "0.7rem",
        fontWeight: 600,
        gap: 0.5,
        cursor: href ? "pointer" : "default",
        textDecoration: "none",
        transition: "all 0.2s ease",
        "&:hover": href
            ? {
                  backgroundColor: hoverBackgroundColor,
                  color: "#ffffff",
              }
            : undefined,
        ...props.sx,
    };

    const chipElement = (
        <Box
            component={href ? "a" : "div"}
            href={href}
            target={href ? "_blank" : undefined}
            rel={href ? "noopener noreferrer" : undefined}
            sx={baseStyles}
            {...props}
        >
            {chipContent}
        </Box>
    );

    return isTruncated ? (
        <Tooltip title={label} arrow placement="top">
            {chipElement}
        </Tooltip>
    ) : (
        chipElement
    );
};
